import React, { useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';

const CODEMIRROR_VERSION = '5.65.16';
const assets = {
  css: `https://cdn.jsdelivr.net/npm/codemirror@${CODEMIRROR_VERSION}/lib/codemirror.min.css`,
  theme: `https://cdn.jsdelivr.net/npm/codemirror@${CODEMIRROR_VERSION}/theme/material-darker.min.css`,
  core: `https://cdn.jsdelivr.net/npm/codemirror@${CODEMIRROR_VERSION}/lib/codemirror.min.js`,
  python: `https://cdn.jsdelivr.net/npm/codemirror@${CODEMIRROR_VERSION}/mode/python/python.min.js`,
  clike: `https://cdn.jsdelivr.net/npm/codemirror@${CODEMIRROR_VERSION}/mode/clike/clike.min.js`,
  javascript: `https://cdn.jsdelivr.net/npm/codemirror@${CODEMIRROR_VERSION}/mode/javascript/javascript.min.js`,
};

let assetPromise;

function loadStylesheet(href) {
  if (document.querySelector(`link[href="${href}"]`)) return;
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = href;
  document.head.appendChild(link);
}

function loadScript(src) {
  const existing = document.querySelector(`script[src="${src}"]`);
  if (existing) return Promise.resolve();

  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.onload = resolve;
    script.onerror = reject;
    document.body.appendChild(script);
  });
}

function loadCodeMirror() {
  if (window.CodeMirror) return Promise.resolve(window.CodeMirror);
  if (!assetPromise) {
    loadStylesheet(assets.css);
    loadStylesheet(assets.theme);
    assetPromise = loadScript(assets.core)
      .then(() => Promise.all([loadScript(assets.python), loadScript(assets.clike), loadScript(assets.javascript)]))
      .then(() => window.CodeMirror);
  }
  return assetPromise;
}

function modeForLanguage(languageKey) {
  return {
    python: 'python',
    javascript: 'javascript',
    cpp: 'text/x-c++src',
    java: 'text/x-java',
  }[languageKey] || 'python';
}

export default function CodeEditor({ value, languageKey, onChange }) {
  const textareaRef = useRef(null);
  const editorRef = useRef(null);
  const onChangeRef = useRef(onChange);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    let cancelled = false;

    loadCodeMirror()
      .then((CodeMirror) => {
        if (cancelled || !textareaRef.current || editorRef.current) return;

        const editor = CodeMirror.fromTextArea(textareaRef.current, {
          mode: modeForLanguage(languageKey),
          theme: 'material-darker',
          lineNumbers: true,
          indentUnit: 4,
          tabSize: 4,
          indentWithTabs: false,
          smartIndent: true,
          viewportMargin: Infinity,
          extraKeys: {
            Tab(instance) {
              if (instance.somethingSelected()) {
                instance.indentSelection('add');
              } else {
                instance.replaceSelection('    ', 'end');
              }
            },
          },
        });

        editor.setSize('100%', '100%');
        editor.on('change', (instance) => onChangeRef.current(instance.getValue()));
        editorRef.current = editor;
        setLoaded(true);
      })
      .catch(() => setLoaded(false));

    return () => {
      cancelled = true;
      if (editorRef.current) {
        editorRef.current.toTextArea();
        editorRef.current = null;
      }
    };
  }, [languageKey]);

  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;
    if (editor.getValue() !== value) {
      editor.setValue(value);
      editor.setCursor({ line: 0, ch: 0 });
      editor.scrollTo(0, 0);
    }
    editor.setOption('mode', modeForLanguage(languageKey));
    requestAnimationFrame(() => {
      editor.refresh();
      editor.scrollTo(0, 0);
    });
  }, [value, languageKey, loaded]);

  return (
    <Box
      sx={{
        height: { xs: 420, md: 'calc(100vh - 360px)' },
        minHeight: 420,
        bgcolor: '#101820',
        '& .CodeMirror': {
          height: '100%',
          fontFamily: '"SFMono-Regular", Menlo, Monaco, Consolas, monospace',
          fontSize: 14,
          lineHeight: 1.55,
        },
        '& .CodeMirror-gutters': {
          borderRight: '1px solid #26343d',
        },
      }}
    >
      <textarea
        ref={textareaRef}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        spellCheck={false}
        style={{
          width: '100%',
          height: '100%',
          resize: 'none',
          border: 0,
          padding: 14,
          background: '#101820',
          color: '#edf4f1',
          fontFamily: '"SFMono-Regular", Menlo, Monaco, Consolas, monospace',
          fontSize: 14,
          lineHeight: 1.55,
        }}
      />
    </Box>
  );
}
